/**
 * RelativityShader - 相对论光速限制效果着色器包装类
 * 
 * 功能：光速限制可视化、长度收缩、红移/蓝移、时间膨胀
 */

import * as Phaser from 'phaser';

// Fragment Shader 源码（嵌入以避免 GLSL 文件加载问题）
const FRAGMENT_SHADER = `
precision mediump float;

uniform float u_velocity;
uniform vec2 u_direction;
uniform vec2 u_center;
uniform float u_time;
uniform sampler2D u_mainTexture;
uniform vec2 u_resolution;

varying vec2 outTexCoord;

const float SPEED_OF_LIGHT = 1.0;

float lorentzFactor(float velocity) {
  float vSquared = min(velocity * velocity, 0.99);
  return 1.0 / sqrt(1.0 - vSquared);
}

vec2 lengthContraction(vec2 coord, vec2 center, vec2 direction, float gamma) {
  vec2 relativeCoord = coord - center;
  float alongDirection = dot(relativeCoord, direction);
  vec2 perpendicular = relativeCoord - alongDirection * direction;
  float contractedAlong = alongDirection / gamma;
  return center + contractedAlong * direction + perpendicular;
}

vec3 redShift(vec3 color, float velocity) {
  float shiftFactor = velocity * 0.5;
  vec3 shifted = color;
  shifted.r = min(1.0, color.r + shiftFactor * 0.3);
  shifted.b = max(0.0, color.b - shiftFactor * 0.3);
  shifted *= (1.0 - shiftFactor * 0.1);
  return shifted;
}

vec3 blueShift(vec3 color, float velocity) {
  float shiftFactor = velocity * 0.5;
  vec3 shifted = color;
  shifted.b = min(1.0, color.b + shiftFactor * 0.3);
  shifted.r = max(0.0, color.r - shiftFactor * 0.2);
  shifted *= (1.0 + shiftFactor * 0.05);
  return shifted;
}

vec3 dopplerShift(vec3 color, float velocity, vec2 direction) {
  float approaching = direction.x;
  if (approaching > 0.0) {
    return blueShift(color, velocity * approaching);
  } else {
    return redShift(color, velocity * abs(approaching));
  }
}

vec3 speedOfLightEffect(vec3 color, float velocity, float time) {
  float threshold = 0.8;
  if (velocity > threshold) {
    float glowIntensity = (velocity - threshold) / (1.0 - threshold);
    float pulse = sin(time * 10.0) * 0.3 + 0.7;
    vec3 glowColor = vec3(1.0, 0.9, 0.8);
    color += glowColor * glowIntensity * pulse * 0.4;
    float edgeGlow = pow(glowIntensity, 2.0) * 0.2;
    color += vec3(edgeGlow);
  }
  return clamp(color, 0.0, 1.0);
}

vec2 timeDilationEffect(vec2 texCoord, float velocity, float time) {
  float gamma = lorentzFactor(velocity);
  if (velocity > 0.5) {
    float dilationFactor = (gamma - 1.0) * 0.02;
    float wave = sin(time * 2.0 + texCoord.y * 10.0) * dilationFactor;
    texCoord.x += wave;
  }
  return texCoord;
}

void main() {
  vec4 baseColor = texture2D(u_mainTexture, outTexCoord);
  if (baseColor.a < 0.01) {
    gl_FragColor = baseColor;
    return;
  }

  float gamma = lorentzFactor(u_velocity);
  vec2 contractedCoord = lengthContraction(
    outTexCoord * u_resolution,
    u_center,
    u_direction,
    gamma
  );
  contractedCoord = contractedCoord / u_resolution;
  contractedCoord = timeDilationEffect(contractedCoord, u_velocity, u_time);
  contractedCoord = clamp(contractedCoord, vec2(0.0), vec2(1.0));

  vec4 contractedColor = texture2D(u_mainTexture, contractedCoord);
  vec3 shiftedColor = dopplerShift(contractedColor.rgb, u_velocity, u_direction);
  shiftedColor = speedOfLightEffect(shiftedColor, u_velocity, u_time);

  gl_FragColor = vec4(shiftedColor, contractedColor.a);
}
`;

// Vertex Shader（标准 PostFX）
const VERTEX_SHADER = `
precision mediump float;

uniform mat4 uProjectionMatrix;
uniform mat4 uViewMatrix;
uniform mat4 uModelMatrix;

attribute vec2 inPosition;
attribute vec2 inTexCoord;
attribute vec4 inTintEffect;
attribute vec4 inTint;

varying vec2 outTexCoord;
varying vec4 outTint;

void main() {
  gl_Position = uProjectionMatrix * uViewMatrix * uModelMatrix * vec4(inPosition, 0.0, 1.0);
  outTexCoord = inTexCoord;
  outTint = inTint;
}
`;

/**
 * 相对论效果参数
 */
export interface RelativityEffectConfig {
  /** 物体速度 / 光速 (0.0 ~ 1.0) */
  velocity: number;
  /** 运动方向（归一化向量） */
  direction: { x: number; y: number };
  /** 物体中心位置（屏幕坐标） */
  center?: { x: number; y: number };
  /** 时间参数（用于动画） */
  time?: number;
}

export class RelativityShader extends Phaser.Renderer.WebGL.Pipelines.PostFXPipeline {
  private currentVelocity: number = 0.0;
  private currentDirection: { x: number; y: number } = { x: 1, y: 0 };
  private currentTime: number = 0.0;

  constructor(game: Phaser.Game) {
    super({
      game,
      name: 'RelativityShader',
      fragShader: FRAGMENT_SHADER,
      vertShader: VERTEX_SHADER,
    });
  }

  override onDraw(): void {
    this.set1f('u_velocity', this.currentVelocity);
    this.set2f('u_direction', this.currentDirection.x, this.currentDirection.y);
    this.set1f('u_time', this.currentTime);

    const width = this.game.config.width as number;
    const height = this.game.config.height as number;
    this.set2f('u_resolution', width, height);
  }

  setEffectConfig(config: RelativityEffectConfig): void {
    this.currentVelocity = Math.max(0.0, Math.min(0.99, config.velocity));

    const dirLen = Math.sqrt(
      config.direction.x * config.direction.x +
      config.direction.y * config.direction.y
    );
    if (dirLen > 0) {
      this.currentDirection = {
        x: config.direction.x / dirLen,
        y: config.direction.y / dirLen,
      };
    }

    if (config.time !== undefined) {
      this.currentTime = config.time;
    }

    if (config.center) {
      this.set2f('u_center', config.center.x, config.center.y);
    }
  }

  setVelocity(velocity: number): void {
    this.currentVelocity = Math.max(0.0, Math.min(0.99, velocity));
  }

  setDirection(x: number, y: number): void {
    const dirLen = Math.sqrt(x * x + y * y);
    if (dirLen > 0) {
      this.currentDirection = { x: x / dirLen, y: y / dirLen };
    }
  }

  setTimeValue(time: number): void {
    this.currentTime = time;
  }

  getLorentzFactor(): number {
    const vSquared = this.currentVelocity * this.currentVelocity;
    if (vSquared >= 1.0) return Infinity;
    return 1.0 / Math.sqrt(1.0 - vSquared);
  }

  getVelocity(): number {
    return this.currentVelocity;
  }

  getDirection(): { x: number; y: number } {
    return this.currentDirection;
  }

  reset(): void {
    this.currentVelocity = 0.0;
    this.currentDirection = { x: 1, y: 0 };
    this.currentTime = 0.0;
  }
}

export function createRelativityShader(game: Phaser.Game): RelativityShader {
  return new RelativityShader(game);
}

export function registerRelativityShader(game: Phaser.Game): void {
  const renderer = game.renderer as Phaser.Renderer.WebGL.WebGLRenderer;

  if (!renderer.pipelines) {
    console.warn('[RelativityShader] WebGL Pipeline 系统不可用');
    return;
  }

  const pipeline = createRelativityShader(game);
  renderer.pipelines.add('RelativityShader', pipeline);
  console.log('[RelativityShader] Pipeline 已注册');
}

export function applyRelativityEffect(
  gameObject: Phaser.GameObjects.GameObject,
  config: RelativityEffectConfig
): void {
  const obj = gameObject as Phaser.GameObjects.Sprite;

  if (typeof obj.setPipeline !== 'function') {
    console.warn('[RelativityShader] 该游戏对象不支持 Pipeline');
    return;
  }

  obj.setPipeline('RelativityShader');

  // 通过 postPipelines 数组获取 pipeline 实例
  const pipelines = obj.postPipelines;
  const pipeline = pipelines.find(p => p.name === 'RelativityShader') as RelativityShader | undefined;
  if (pipeline && typeof pipeline.setEffectConfig === 'function') {
    pipeline.setEffectConfig(config);
  }
}